import React from 'react';
import { connect } from 'react-redux';
import GuidePage from './GuidePage';
import NewAgreementButton from './NewAgreementButton';
import Link from './Link';
import './Examples.css';
import domainTransferImg from '../domaintransfer2.png';
import stairwayImg from '../stairway.png';
import towelVestImg from '../towelvest.png';
import runEveryDayImg from '../runeveryday.png';
import securityDepositImg from '../securitydeposit.png';
import secretAgreementImg from '../secretagreement.png';
import insuranceImg from '../insurance.png';

const examples = [
  {
    id: 'domain-transfers', title: "Domain name transfers",
    image: domainTransferImg, width: 418, height: 511, alt: 'Domain name transfer',
    content: <>
        <p>The easier it is to prove whether the terms of your agreement have been satisfied the better Atstake will work.</p>
        <p>Domain name transfers are an example of something easy for anyone to verify, which means that any arbitrator who cares
          about their reputation is very unlikely to give an incorrect ruling for this type of agreement.</p>
        <p>Existing domain name <Link noTarget={true} rel="noopener noreferrer" href="https://domainnamewire.com/2014/01/21/domain-escrow-price-comparison/">escrow services</Link> charge hundreds
          and sometimes thousands of USD for their services even if the transaction completes without a dispute.</p>
        <p>A similar example is online ticket reselling.
          For tickets whose transfer can be independently verified, Atstake allows for safe ticket sales without <Link noTarget={true} rel="noopener noreferrer" href="https://www.stubhub.com/">Stubhub's</Link> high fees.</p>
    </>
  },
  {
    id: 'in-person-services', title: "In-person services",
    image: stairwayImg, width: 408, height: 597, alt: 'Stairway repair',
    content: <>
        <p>For services that can't be performed immediately when money is exchanged, the service provider may worry about not being fully paid and the purchaser of the service may worry
          that the quality of the service may be less than what was agreed on.</p>
        <p>Atstake solves this by ensuring that the funds are locked up and can't
          be moved by either party until an agreement is reached or the arbitrator approves.</p>
    </>
  },
  {
    id: 'non-performance', title: "Non-performance penalties",
    image: towelVestImg, width: 406, height: 620, alt: 'Towel-vest website',
    content: <>
        <p>This agrement shows a penalty for non-performance. If the buyer needs the website before a certain date
          they can request that the service provider forfeits a bond if they fail to complete the website on time. Without this penalty the
          service provider may be too tempted to not do any work at all if a better opportunity presents itself.</p>
        <p>Note that this agreement refers to an external document. This is safe if the content of this document could be proven to the arbitrator if necessary.
          To make these proofs even more reliable the hash of a document's content can be included.</p>
    </>
  },
  {
    id: 'personal-goals', title: "Achievement of personal goals",
    image: runEveryDayImg, width: 399, height: 560, alt: 'Running',
    content: <>
        <p>Atstake can be used to hold yourself accountable for achieving your goals.
          This is the idea
          behind <Link noTarget={true} rel="noopener noreferrer" href={"https://stickk.com"}>Stickk</Link>.
          Atstake charges no fees beyond what it costs to use the Ethereum network (~$0.25 USD per agreement) whereas Stickk
          charges 20% of the amount transferred (and 50% if transferred to a charity).</p>
    </>
  },
  {
    id: 'security-deposits', title: "Security deposits",
    image: securityDepositImg, width: 401, height: 640, alt: 'Security deposit',
    content: <>
        <p>You may have an expensive item that you're willing to let someone use as long as you know that it won't be difficult to get them to
          pay for any damages. Pursing claims after the fact without a security deposit can be a huge hassle. Atstake can give you peace of
          mind in these situations with almost no set-up cost, while also not requiring the other person to trust you with unilateral control
          of their deposit.</p>
    </>
  },
  {
    id: 'insurance', title: "Insurance",
    image: insuranceImg, width: 397, height: 494, alt: 'Insurance',
    content: <>
        <p>Sellers of insurance don't generally lock up their potential payouts for every insurance policy. This would often require locking up
          too much capital, and their reputations and the threat of legal recourse is usually enough for people to trust that these companies
          will pay out when they should.</p>
        <p>However in some cases insurance where both sides lock up funds might be the best
          option -- for instance in areas where people aren't integrated into the existing financial system and in cases where
          the probability of a payout is relatively high.</p>
        <p>We aren't confident that Atstake will find a use case here, but we think it's an interesting possibility.</p>
    </>
  },
  {
    id: 'secret-agreements', title: "Secret agreements",
    image: secretAgreementImg, width: 397, height: 470, alt: 'Secret agreement',
    content: <>
        <p>You can hide the details of your agreements from other people by marking their visibility 'Ratings only' or 'Private'.
          However the text of these agreements is still available to Atstake (so that we can show it to your counterparty and the arbitrator).</p>
        <p>If you and your counterparty prefer to coordinate outside of Atstake for the contract details, you can keep these details hidden even
          from Atstake
          by <Link noTarget={true} rel="noopener noreferrer" href={"https://passwordsgenerator.net/sha256-hash-generator/"}>hashing</Link> the
          information as shown above. This also allows you to only reveal these details to the arbitrator if
          there's a dispute.</p>
    </>
  }
];

function Examples({ hash }) {
  return (
    <GuidePage page="/examples" eyebrow="Example agreements"
      title="Example agreements"
      intro={"Atstake is a general tool which can be used for many types of contracts. The examples below illustrate Atstake's flexibility and will hopefully give you ideas for how to use it."}
      sections={examples.map(example => ({ id: example.id, label: example.title }))} hash={hash}>
      <div className="agreementHomeInner guideBody">
        <p className="exampleIntroNote">These examples have simple descriptions for brevity.
          Depending on your circumstance you may want to specify your contracts in more detail.</p>
        {examples.map((example, index) => (
          <section className="exampleCase" id={example.id} key={example.id} aria-labelledby={example.id + '-title'}>
            <div>
              <div className="agreementEyebrow">Example {String(index + 1).padStart(2, '0')}</div>
              <h2 id={example.id + '-title'}>{example.title}</h2>
              <div className="guideCopy">{example.content}</div>
            </div>
            <figure className="exampleFigure">
              <img src={example.image} width={example.width} height={example.height} alt={example.alt + ' — sample agreement terms'} loading="lazy" />
            </figure>
          </section>
        ))}
        <section className="exampleClosing" aria-labelledby="your-agreement-title">
          <h2 id="your-agreement-title">Other agreements</h2>
          <p>This is just a small sample of what's possible with Atstake. You can create any agreement that involves either one or two parties staking funds,
          then these funds being distributed based on any criteria.</p>
          <div className="agreementHomeActions">
            <NewAgreementButton />
            <a className="agreementTextLink" href="/help#beforefunding">Before committing funds <span aria-hidden="true">→</span></a>
          </div>
        </section>
      </div>
    </GuidePage>
  );
}

export default connect(state => ({ hash: state.menu.hash }))(Examples);
